import { useEffect, useState, RefObject } from 'react';

/**
 * Custom hook that monitors container width using ResizeObserver.
 * Returns the current width of the container element in pixels.
 * 
 * @param containerRef - React ref to the container element
 * @returns Current container width in pixels, or 0 if container is not available
 */
export function useContainerWidth(containerRef: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      setWidth(0);
      return;
    }

    // Set initial width
    setWidth(container.offsetWidth);

    // Create ResizeObserver to monitor width changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        setWidth(newWidth);
      }
    });

    // Start observing
    resizeObserver.observe(container);

    // Cleanup on unmount
    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  return width;
}
